import {useCallback, useRef, useState} from 'react'
import {useIntl} from 'react-intl'
import UseRoutesPermission from '../../../../_gori/hooks/UseRoutesPermission'
import {OrderStatus} from '../core/_const'

type OrderRowActionsOptions = {
  onAfterCancel?: () => void
  onAfterAssign?: () => void
  onAfterRefund?: () => void
  onAfterHold?: () => void
}

type ActionHandlers = {
  onPrintLabel?: (orderData: any) => void
  onPrintPackingSlip?: (orderData: any) => void
  onCopy?: (orderData: any) => void
}

type GetActionMenuItemsOptions = {
  disabled?: boolean
  loading?: boolean
  handlers?: ActionHandlers
  /** Hide items where the refund exists (used for packing slip in list view) */
  refund?: any
}

export type OrderModalShowState = {
  addToBatch: boolean
  assignTo: boolean
  cancelOrder: boolean
  holdOrder: boolean
  requestRefund: boolean
  requestRefundAction: boolean
}

export type OrderModalState = {
  showModal: OrderModalShowState
  addToBatch: any[]
  assignTo: any[]
  cancelOrder: any[]
  holdOrder: any[]
  requestRefund: any[]
}

export type OrderModalHandlers = {
  closeAddToBatch: () => void
  closeAssignTo: () => void
  closeCancelOrder: () => void
  closeHoldOrder: () => void
  closeRequestRefund: () => void
  confirmRefund: () => void
  closeRequestRefundAction: () => void
  onAfterAssign: () => void
  onAfterCancel: () => void
  onAfterHold: () => void
}

export const useOrderRowActions = (options: OrderRowActionsOptions = {}) => {
  const intl = useIntl()
  const {routes} = UseRoutesPermission()
  const optionsRef = useRef(options)
  optionsRef.current = options

  const [showModal, setShowModal] = useState<OrderModalShowState>({
    addToBatch: false,
    assignTo: false,
    cancelOrder: false,
    holdOrder: false,
    requestRefund: false,
    requestRefundAction: false,
  })

  const [data, setData] = useState<{
    addToBatch: any[]
    assignTo: any[]
    cancelOrder: any[]
    holdOrder: any[]
    requestRefund: any[]
  }>({
    addToBatch: [],
    assignTo: [],
    cancelOrder: [],
    holdOrder: [],
    requestRefund: [],
  })

  const openAddToBatch = useCallback((orders: any[]) => {
    setData((prev) => ({...prev, addToBatch: orders}))
    setShowModal((prev) => ({...prev, addToBatch: true}))
  }, [])

  const openAssignTo = useCallback((orders: any[]) => {
    setData((prev) => ({...prev, assignTo: orders}))
    setShowModal((prev) => ({...prev, assignTo: true}))
  }, [])

  const openCancelOrder = useCallback((orders: any[]) => {
    setData((prev) => ({...prev, cancelOrder: orders}))
    setShowModal((prev) => ({...prev, cancelOrder: true}))
  }, [])

  const openHoldOrder = useCallback((orders: any[]) => {
    setData((prev) => ({...prev, holdOrder: orders}))
    setShowModal((prev) => ({...prev, holdOrder: true}))
  }, [])

  const openRequestRefund = useCallback((orders: any[]) => {
    setData((prev) => ({...prev, requestRefund: orders}))
    setShowModal((prev) => ({...prev, requestRefund: true}))
  }, [])

  // --- Modal close / callback handlers ---

  const closeAddToBatch = useCallback(() => {
    setShowModal((prev) => ({...prev, addToBatch: false}))
  }, [])

  const closeAssignTo = useCallback(() => {
    setShowModal((prev) => ({...prev, assignTo: false}))
  }, [])

  const closeCancelOrder = useCallback(() => {
    setShowModal((prev) => ({...prev, cancelOrder: false}))
  }, [])

  const closeHoldOrder = useCallback(() => {
    setShowModal((prev) => ({...prev, holdOrder: false}))
  }, [])

  const closeRequestRefund = useCallback(() => {
    setShowModal((prev) => ({...prev, requestRefund: false}))
  }, [])

  const confirmRefund = useCallback(() => {
    setShowModal((prev) => ({
      ...prev,
      requestRefund: false,
      requestRefundAction: true,
    }))
  }, [])

  const closeRequestRefundAction = useCallback(() => {
    setShowModal((prev) => ({...prev, requestRefundAction: false}))
    optionsRef.current.onAfterRefund?.()
  }, [])

  const onAfterAssign = useCallback(() => {
    optionsRef.current.onAfterAssign?.()
  }, [])

  const onAfterCancel = useCallback(() => {
    optionsRef.current.onAfterCancel?.()
  }, [])

  const onAfterHold = useCallback(() => {
    optionsRef.current.onAfterHold?.()
  }, [])

  // --- Action menu builder ---

  const getActionMenuItems = useCallback(
    (orderData: any, opts: GetActionMenuItemsOptions = {}) => {
      const {disabled = false, loading = false, handlers = {}, refund} = opts
      const items: any[] = []
      const guard = !disabled && !loading

      // Print Label — only for shipped orders (handler-gated)
      if (handlers.onPrintLabel) {
        items.push({
          key: 'printLabel',
          label: intl.formatMessage({id: 'PRINT_LABEL'}),
          action: () => {
            if (guard) handlers.onPrintLabel!(orderData)
          },
          hidden: orderData?.status !== OrderStatus.SHIPPED,
        })
      }

      // Print Packing Slip (handler-gated)
      if (handlers.onPrintPackingSlip) {
        items.push({
          key: 'printPackingSlip',
          label: intl.formatMessage({id: 'PRINT_PACKING_SLIP'}),
          action: () => {
            if (guard) handlers.onPrintPackingSlip!(orderData)
          },
          hidden:
            !routes.SHIPMENTS_PACKING_SLIP.hasPermission || (refund !== undefined && !!refund),
        })
      }

      // Copy (handler-gated)
      if (handlers.onCopy) {
        items.push({
          key: 'copy',
          label: intl.formatMessage({id: 'COPY'}),
          action: () => {
            if (guard) handlers.onCopy!(orderData)
          },
          hidden: !routes.ORDERS_CREATE.hasPermission,
        })
      }

      // Add to Batch
      items.push({
        key: 'addToBatch',
        label: intl.formatMessage({id: 'ADD_TO_BATCH'}),
        action: () => {
          if (guard) openAddToBatch([{original: orderData}])
        },
        hidden: !routes.BATCHES_UPDATE.hasPermission,
      })

      // Assign To
      items.push({
        key: 'assignTo',
        label: intl.formatMessage({id: 'ASSIGN_TO'}),
        action: () => {
          if (guard) openAssignTo([{original: orderData}])
        },
        hidden: !routes.ORDERS_ASSIGN.hasPermission,
      })

      // Hold
      items.push({
        key: 'hold',
        label: intl.formatMessage({id: 'HOLD'}),
        action: () => {
          if (guard) openHoldOrder([{original: orderData}])
        },
        hidden: orderData?.status !== OrderStatus.OPEN || !routes.ORDERS_HOLD.hasPermission,
      })

      // Cancel
      items.push({
        key: 'cancel',
        label: intl.formatMessage({id: 'CANCEL'}),
        action: () => {
          if (guard) openCancelOrder([{original: orderData}])
        },
        hidden: orderData?.status !== OrderStatus.OPEN || !routes.ORDERS_CANCEL.hasPermission,
      })

      // Request Refund
      items.push({
        key: 'requestRefund',
        label: intl.formatMessage({id: 'REQUEST_REFUND'}),
        action: () => {
          if (guard) {
            const shipmentId =
              orderData?.shipments?.[0]?.gori_shipment_id ||
              orderData?.latest_shipment?.gori_shipment_id
            if (!shipmentId) return
            openRequestRefund([{original: {...orderData, id: shipmentId}}])
          }
        },
        hidden: orderData?.status !== OrderStatus.SHIPPED || !routes.SHIPMENTS_REFUND.hasPermission,
      })

      return items
    },
    [intl, routes, openAddToBatch, openAssignTo, openCancelOrder, openHoldOrder, openRequestRefund]
  )

  // --- Composed state & handlers for the modal component ---

  const dataModal: OrderModalState = {
    showModal,
    addToBatch: data.addToBatch,
    assignTo: data.assignTo,
    cancelOrder: data.cancelOrder,
    holdOrder: data.holdOrder,
    requestRefund: data.requestRefund,
  }

  const modalHandlers: OrderModalHandlers = {
    closeAddToBatch,
    closeAssignTo,
    closeCancelOrder,
    closeHoldOrder,
    closeRequestRefund,
    confirmRefund,
    closeRequestRefundAction,
    onAfterAssign,
    onAfterCancel,
    onAfterHold,
  }

  return {
    openAddToBatch,
    openAssignTo,
    openCancelOrder,
    openHoldOrder,
    openRequestRefund,
    getActionMenuItems,
    dataModal,
    modalHandlers,
  }
}
